import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import { ClientConfiguration } from '../models/client-configuration';
import { ApiService } from './api.service';
import { ConfigService } from './config.service';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';
import { LocaleService } from './locale.service';
import { SupportService } from './support.service';
import { StorageService } from './storage.service';
import { FingerprintService } from './fingerprint.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class InitService {
  constructor(
    private logger: LoggerService,
    private apiService: ApiService,
    private storageService: StorageService,
    private localeService: LocaleService,
    private supportService: SupportService,
    private configService: ConfigService,
    private authService: AuthService,
    private fingerprintService: FingerprintService
  ) {
    if (environment.disableCache) {
      this.logger.warn('HTTP cache disabled!');
    }
  }

  getConfiguration(): Promise<ClientConfiguration> {
    return new Promise((resolve, _reject) => {
      const user = this.authService.getLoggedUser();
      const patronId = user === null ? null : user.id;
      this.apiService
        .getClientConfiguration(this.localeService.language, patronId)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.storageService.remove('user');
              this.apiService.logout().pipe(
                finalize(() => {
                  this.apiService.getClientConfiguration(this.localeService.language).subscribe(async (config) => {
                    await this.processConfiguration(config, resolve);
                  })
                })
              ).subscribe();
              return of(null);
            }
            return throwError(error);
          })
        )
        .subscribe(
          async (config: ClientConfiguration) => {
            await this.processConfiguration(config, resolve);
          },
          (err) => {
            this.configService.initError = true;
            resolve(err);
          }
        );
    });
  }

  private async processConfiguration(
    config: ClientConfiguration,
    resolve: (value?: PromiseLike<ClientConfiguration> | ClientConfiguration) => void
  ) {
    if (config === null) {
      return;
    }
    this.configService.init(config);
    this.configService.devMode = !environment.production;
    this.storageService.init(config.useSessionStorage);
    this.supportService.init(this.localeService.language);

    if (config.deviceControlViaWeb) {
      await this.fingerprintService.init();
    }

    if (
      this.configService.isCeibal() &&
      !this.storageService.get('user') &&
      !window.location.href.includes('login/callback')
    ) {
      this.checkCeibalCasSession();
    }

    resolve(config);
  }

  checkCeibalCasSession() {
    this.apiService.redirectToCastLogin().subscribe(
      (data) => {
        if (data.url.startsWith('https://bibliotecapais.ceibal.edu.uy')) {
          window.location.href = 'https://bibliotecapais.ceibal.edu.uy/opac/authCAS?app=opac';
        }
      },
      (err) => {
        // We have to remove this error handling. Because it's sloppy. We must correctly configure our cloudfront.
        window.location.href = 'https://bibliotecapais.ceibal.edu.uy/opac/authCAS?app=opac';
      }
    );
  }
}
